// docs/72 IT18 — the catalogue's abilities (a worn piece's active, what a used-up piece casts) for
// the hotbar. `fk-sim-wasm.abilities()` hands over the heroes' table only, so these are built from
// items.gen.json in the same shape `fk_grammar::Ability` serialises to, mirroring tools/items/build.mjs
// (`shape`, `effect`) exactly; the hotbar's targeting, preview and tooltip then treat them like any
// spell, and `legal()` (the sim) still judges every one.
import type { WasmAbility } from '../sim/actions';
import { catalogueItems, fightUse, itemSets, upgradeAbilityId, upgradeSpecs, ITEM_ABILITY_BASE, SET_ABILITY_BASE, type AbilitySpec, type CatalogueItem, type DmgSpec, type EffectSpec } from './catalogue';

const cap = (s: string): string => s.split('_').map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w)).join('');
const HELPFUL_TO_FRIEND = (e: EffectSpec): boolean => {
  const [k] = Object.keys(e); const v = e[k!] as Record<string, unknown> | string | number | boolean;
  if (k === 'heal' || k === 'status' || k === 'clear') return (v as Record<string, unknown>)['to'] === 'allies';
  return k === 'cleanse' ? v === 'allies' : k === 'resource';
};

function dmg(d: DmgSpec): unknown {
  if (d === 'weapon') return 'Weapon';
  if ('spell' in d) return { Spell: d.spell };
  if ('flat' in d) return { Flat: d.flat };
  return { WeaponPlus: d.weapon_plus };
}

function effect(e: EffectSpec): unknown {
  const [k] = Object.keys(e);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the catalogue's JSON, checked by tools/items/check.mjs
  const v = e[k!] as any;
  switch (k) {
    case 'damage': return { Damage: { dmg: dmg(v.dmg as DmgSpec), to: cap(String(v.to)) } };
    case 'heal': return { Heal: { amount: v.amount, to: cap(String(v.to)) } };
    case 'push': return { Push: { tiles: v } };
    case 'pull': return { Pull: { tiles: v } };
    case 'status': return { Status: { kind: cap(String(v.kind)), magnitude: v.magnitude, pulses: v.kind === 'hidden' ? 0 : v.pulses, to: cap(String(v.to)) } };
    case 'clear': return { ClearStatus: { kind: cap(String(v.kind)), to: cap(String(v.to)) } };
    case 'cleanse': return { Cleanse: { to: cap(String(v)) } };
    case 'teleport': return { Teleport: { tiles: v } };
    case 'move_bonus': return { MoveBonus: { tiles: v } };
    case 'charge': return { MoveSelf: { tiles: v } };
    case 'hazard': return { HazardTiles: { kind: 'Burning', magnitude: v.magnitude, pulses: v.pulses } };
    case 'trap': return { Trap: { dmg: v.dmg, root_pulses: v.root_pulses, pulses: v.pulses } };
    case 'revive': return { Revive: { hp: v.hp } };
    case 'resource': return { Resource: { amount: v } };
    case 'reveal': return 'Reveal';
    default: return k ?? '';
  }
}

/** `build.mjs shape()`: the Rust `Area` and `Range` of an item ability. */
export function shapeOf(a: AbilitySpec): Pick<WasmAbility, 'area' | 'range'> {
  const area = a.area ?? 'single';
  const areaW: WasmAbility['area'] = area === 'single' ? 'Single' : area === 'self' ? 'SelfUnit' : area === 'adjacent' ? 'Adjacent'
    : 'blast' in area ? { Blast: { radius: area.blast } } : 'line' in area ? { Line: { len: area.line } } : { AlliesWithin: area.allies_within };
  const r = a.range;
  let range: WasmAbility['range'];
  if (r === 'self' || area === 'self' || area === 'adjacent' || (typeof area === 'object' && 'allies_within' in area)) range = { min: 0, max: 'SelfOnly', los: false };
  else if (r === 'reach') range = { min: 1, max: 'Reach', los: false };
  else if (r === 'weapon') range = { min: 1, max: 'Weapon', los: true };
  else {
    const blast = typeof area === 'object' && 'blast' in area, line = typeof area === 'object' && 'line' in area;
    const onFriend = a.effects.some(HELPFUL_TO_FRIEND);
    const moves = a.effects.some((e) => 'teleport' in e || 'revive' in e);
    const min = r === 0 || blast ? 0 : line ? 1 : moves ? 1 : onFriend ? 0 : 1;
    range = { min, max: { Tiles: r }, los: a.los ?? true };
  }
  return { area: areaW, range };
}

/** One item ability as `fk_grammar::Ability` serialises it. `used`: cast by using the piece up. */
export function itemWasmAbility(it: CatalogueItem | undefined, a: AbilitySpec, used: boolean, id = ITEM_ABILITY_BASE + (it?.id ?? 0)): WasmAbility {
  return {
    id, name: a.name, level_req: 1,
    slot: used || a.slot === 'bonus' ? 'Bonus' : 'Action',
    ...shapeOf(a),
    effects: a.effects.map(effect), cooldown: used ? 0 : a.cooldown ?? 0, resource_cost: a.cost ?? 0, per_pulse: 1, per_adventure: 0, requires: [],
  };
}

/** Every catalogue piece's ability, by id. */
export function itemWasmAbilities(): WasmAbility[] {
  const out: WasmAbility[] = [];
  for (const it of catalogueItems()) {
    if (it.active) out.push(itemWasmAbility(it, it.active, false));
    const f = fightUse(it);
    if (f) out.push(itemWasmAbility(it, f, true));
  }
  // docs/110: each set's ability, at 15000 + its id
  for (const s of itemSets()) out.push(itemWasmAbility(undefined, s.bonus.active, false, SET_ABILITY_BASE + s.id));
  // docs/112: every rank of each upgrade's ability, at 16000 + 5 × its id + rank − 1
  for (const u of upgradeSpecs()) (u.active ?? []).forEach((a, i) => out.push(itemWasmAbility(undefined, a, false, upgradeAbilityId(u.id, i + 1))));
  return out;
}
